import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ButtonEdit } from '../../../general/mini_components';
import { Box, Card, Chip, Typography, Grid, IconButton } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import styled from 'styled-components';
import { Alert } from "@mui/lab";
import { Translate, withLocalize } from 'react-localize-redux';
import File from '../../../general/File';
import EncryptedImage from '../../../general/EncryptedImage';
import SmartField from '../../../general/SmartFields';
import { fullDateFromPostgresString, getData, isSmartField, localStringDateFromTimeStamp, stringDatePostgresToDate } from '../../../../utils/index.jsx';
import { ALL_SMARTFIELDS_TYPES, MEDICAL_HISTORY_FIELDS } from '../../../../constants';
import FieldGroupMultioption from '../../../general/FieldGroupMultioption';
import { isMultioptionFieldType, isNotAvailableImageValue, optionValues, resolveFieldType } from '../../../../utils/fieldValueCoercion';
import { isClosedGroup } from '../../../../utils/fieldGroupEdit';
import {
    describeDiagnosisValue, diagnosisLabelsFromRecords, isIcd11Field, namedCodeOptions, unnamedDiagnosisCodes, withCatalogueNames,
} from '../../../../utils/icd11Display';
import { useIcd11CatalogueNames } from '../../../../hooks/useIcd11CatalogueNames';

/**
 * Component that shows all the records/submissions of a section of a patient in a survey
 */


const CardPadding = styled(Card)`
    padding:1rem;
    margin-bottom:1rem;
`;
const GridPadded = styled(Grid)`
    padding:${props => (props.padding ? '0rem 0rem 1rem 1rem' : '0rem')};
`;
const HeaderSection = styled.div`
    display:flex;

`;
function ShowRecordsSection(props) {
    //let [indexSubmission, setIndexSubmission ] = useState(0);
    let [error, setError] = useState(false);

    function editSection(uuidSection){
    
        props.callBackEditSubmission(props.idSubmission, uuidSection);
    }

    // SKIN-612 — code -> name: the labels every ICD-11 pick of this section stores, then the
    // API's WHO catalogue for the codes those leave unnamed (only those codes are sent).
    const groupRecords = (props.fieldGroups || [])
        .flatMap(fg => (fg.instances || []).flatMap(instance => instance.surveyRecords || []));
    const storedLabels = diagnosisLabelsFromRecords([...(props.records || []), ...groupRecords]);
    const groupFieldsById = new Map((props.section?.fieldGroups || []).flatMap(g => g.fields || []).map(f => [f.id, f]));
    const diagnosisValues = [
        ...(props.records || []).filter(r => isIcd11Field(r.surveyField)),
        ...groupRecords.filter(r => isIcd11Field(groupFieldsById.get(r.surveyField?.id))),
    ].map(r => r.value);
    const catalogueNames = useIcd11CatalogueNames(props.uuidInvestigation, unnamedDiagnosisCodes(diagnosisValues, storedLabels));
    function sectionDiagnosisLabels(){
        return withCatalogueNames(storedLabels, catalogueNames);
    }

    function renderValue(valueRecord, field, hideField = false, forceShowLabel = false){
        const showField = forceShowLabel || (props.section.fields.length > 1 && !hideField);
        if(!valueRecord || !valueRecord.value){
            return(
            <Grid item paddingLeft={3}>
                {
                    showField &&
                    <Typography variant="h6" color="textPrimary">
                        {field.name}: 
                    </Typography>
                }
                {
                    ALL_SMARTFIELDS_TYPES.includes(field.type) &&
                    <Typography variant="body2">
                        <Translate id="general.no" />
                    </Typography>
                }
                {
                    !ALL_SMARTFIELDS_TYPES.includes(field.type) &&
                    "-"
                }
            </Grid>)
        }
        if(field.type === "textarea" || MEDICAL_HISTORY_FIELDS.includes(field.type)){
            let value;
            if(field.type === "textarea" || field.type === "medical_history_template"){
                value = valueRecord.value || '';
            }
            else{
            
                // For medical history fields, the value is stored in an array with medical_history_ai property
                value = valueRecord.value && Array.isArray(valueRecord.value) && valueRecord.value.length > 0 
                    ? valueRecord.value[0].medical_history_ai || valueRecord.value[0]['template_medical_history-notes'] || ''
                    : '';
            }
            
            //const parsedValue = value.replace('<h1>','').replace('</h1>','').replace('<h2>','').replace('</h2>','');;
            const StyledDiv = styled.div`
                * {
                    white-space: normal
                }
                
                &.ql-editor p {
                    margin-bottom: 0.25em;
                    line-height: 1.2;
                }
            `;
            return(
            <Grid item paddingLeft={3}>
                {
                    showField &&
                    <Typography variant="h6" color="textPrimary" fontWeight='800'>
                        {field.name}: 
                    </Typography>
                }
                <Typography variant="body2">
                    <StyledDiv className='ql-editor' dangerouslySetInnerHTML={{__html: value || "-"}}></StyledDiv>                    
                </Typography>
            </Grid>
            );
        }
        else if(valueRecord.surveyField.type === "file" || valueRecord.surveyField.type === "image"){
            let fileValue = valueRecord.value;
            if (typeof fileValue === 'string') {
                try { fileValue = JSON.parse(fileValue); } catch(e) { fileValue = []; }
            }
            if (!Array.isArray(fileValue) || fileValue.length === 0) {
                return (
                    <Grid item paddingLeft={3}>
                        {showField && <Typography variant="h6" color="textPrimary">{field.name}: </Typography>}
                        -
                    </Grid>
                );
            }
            // SKIN-646: the operator ticked e.g. "No healthy skin available" in the app.
            if (isNotAvailableImageValue(fileValue)) {
                return (
                    <Grid item paddingLeft={3}>
                        {showField && <Typography variant="h6" color="textPrimary">{field.name}: </Typography>}
                        <Typography variant="body2">
                            {field.notAvailable?.label || <Translate id="hospital.fg-edit-image-not-available" />}
                        </Typography>
                    </Grid>
                );
            }
            if (fileValue[0]?.type === 'encrypted_ref' && props.uuidInvestigation) {
                return <EncryptedImage
                    uuidInvestigation={props.uuidInvestigation}
                    encryptedRefs={fileValue}
                    label={showField ? field.name : undefined}
                />;
            }
            return <File key={valueRecord.id} mode="show" value={fileValue}
                    label={field.name}  />
        }
        else if(field?.type === "instance-selector" || valueRecord.surveyField?.type === "instance-selector"){
            // SKIN-689 — a section-D block's "Applies to lesions": the stored value is the set of
            // referenced C-Lesion instance uuids. This per-section show view has no access to the
            // lesions' own section to name them, so it states how many this block covers; the
            // per-lesion resolution is done server-side (QC diagnosticWorkup) / in the shared
            // dataset resolver (SKIN-692).
            const uuids = optionValues(valueRecord.value);
            return (
                <Grid item paddingLeft={3}>
                    {showField && <Typography variant="h6" color="textPrimary">{field.name}: </Typography>}
                    <Typography variant="body2" gutterBottom>
                        {uuids.length === 0
                            ? '-'
                            : <Translate id="hospital.show-applies-to-lesions-count" data={{ count: uuids.length }} />}
                    </Typography>
                </Grid>
            );
        }
        else if(valueRecord.surveyField.type === "appointment"){
            const dateAppointment = stringDatePostgresToDate(valueRecord.value);
            return (
                <Grid item paddingLeft={3}>
                <Typography variant="h6" color="textPrimary">
                    <Translate id="general.date" />: {dateAppointment.toLocaleString(props.activeLanguage.code,{
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                        })}
                </Typography>
                </Grid>
            )
        }
        else if(isMultioptionFieldType(resolveFieldType(field, valueRecord))){
            // SKIN-612: a forwarded field (lesion_diagnoses) stores bare codes and its options
            // only exist while the source field is being edited; name each stored code from
            // the ICD-11 picks of this section instead.
            const shownField = field?.forwardedFrom
                ? { ...field, options: namedCodeOptions(valueRecord.value, sectionDiagnosisLabels()) }
                : field;
            return (
                <FieldGroupMultioption
                    field={shownField}
                    value={valueRecord.value}
                    mode="show"
                />
            );
        }
        else if(field?.type === "icd11-code" || valueRecord.surveyField?.type === "icd11-code"){
            // SKIN-612: each pick as "code — name"; a pick stored without a label is named like
            // a lesion code, and is never printed as JSON.
            const codes = describeDiagnosisValue(valueRecord.value, sectionDiagnosisLabels());
            return (
                <Grid item paddingLeft={3}>
                    { showField &&
                        <Typography variant="h6" color="textPrimary">
                            {field.name}:
                        </Typography>
                    }
                    <Typography variant="body2" gutterBottom>
                        { codes.length > 0 ? codes.join(', ') : '-' }
                    </Typography>
                </Grid>
            );
        }
        else if(valueRecord.surveyField.type === "text_list"){
            // SkincAIr Flutter sends this as either ["a","b",…] or
            // [{text_list:"a"},{text_list:"b"},…] depending on storage round-trip.
            let entries = valueRecord.value;
            if (typeof entries === 'string') {
                try { entries = JSON.parse(entries); } catch (e) { entries = [entries]; }
            }
            const items = Array.isArray(entries)
                ? entries.map(e => (e && typeof e === 'object' ? (e.text_list ?? JSON.stringify(e)) : String(e)))
                : [String(entries ?? '')];
            return (
                <Grid item paddingLeft={3}>
                    {showField && (
                        <Typography variant="h6" color="textPrimary">
                            {field.name}:
                        </Typography>
                    )}
                    {items.length === 0
                        ? <Typography variant="body2" gutterBottom>-</Typography>
                        : items.map((it, i) => (
                            <Typography key={i} variant="body2" gutterBottom>{it}</Typography>
                          ))}
                </Grid>
            );
        }
        else if(isSmartField(valueRecord.surveyField.type)){
            return(
                <Grid item paddingLeft={3}>
                    <SmartField type={valueRecord.surveyField.type} mode="show" 
                        initialState={{listElements:valueRecord.value}} printMode={props.printMode}
                        label={valueRecord.surveyField.label}  />
                </Grid>)
        }
        else if(typeof valueRecord.value.getMonth === 'function' ){
            return(
                <Grid item paddingLeft={3}>
                { showField &&
                    <Typography variant="h6" color="textPrimary">
                        {field.name}: 
                    </Typography>
                }
                <Typography variant="body2" gutterBottom>
                    { valueRecord.value.toISOString() }
                </Typography>
            </Grid>
            );
        }   
        else if(valueRecord.surveyField.type === "date"){
            
            return (
                <Grid item paddingLeft={3}>
                    { showField &&
                        <Typography variant="h6" color="textPrimary">
                            {field.name}: 
                        </Typography>
                    }
                    <Typography variant="body2" gutterBottom>
                        { localStringDateFromTimeStamp( props.activeLanguage.code, valueRecord.value) }
                    </Typography>
                </Grid>   
            )
        }
        else if (
            (() => {
                let v = valueRecord.value;
                if (typeof v === 'string' && v.startsWith('{')) {
                    try { v = JSON.parse(v); } catch (e) { return false; }
                }
                return v && typeof v === 'object' && !Array.isArray(v) && typeof v.otherText === 'string' && v.otherText.length > 0;
            })()
        ){
            let parsed = valueRecord.value;
            if (typeof parsed === 'string') {
                try { parsed = JSON.parse(parsed); } catch (e) { parsed = {}; }
            }
            const selectedOption = (field.options || []).find(o => o.value === parsed.value);
            const selectedLabel = selectedOption?.label ?? parsed.value;
            return (
                <Grid item paddingLeft={3}>
                    { showField &&
                        <Typography variant="h6" color="textPrimary">
                            {field.name}:
                        </Typography>
                    }
                    <Typography variant="body2" gutterBottom>
                        {selectedLabel} (<em>{parsed.otherText}</em>)
                    </Typography>
                </Grid>
            );
        }
        else if(
            valueRecord.value !== null && valueRecord.value !== undefined
            && typeof valueRecord.value !== 'object'
            && !(typeof valueRecord.value === 'string' && /^\s*[[{]/.test(valueRecord.value))
            && Array.isArray(field.options) && field.options.length > 0 && field.options.length <= 15
        ){
            // Single-value option field (select / radio): show the full
            // option set — selected one highlighted, the rest greyed — so a
            // reviewer sees what could have been chosen. Capped at 15 options
            // so a long searchable-select degrades to just the label below.
            const selectedValue = String(valueRecord.value);
            return (
                <Grid item paddingLeft={3}>
                    { showField &&
                        <Typography variant="h6" color="textPrimary">
                            {field.name}:
                        </Typography>
                    }
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        { field.options.map((o) => {
                            const selected = String(o.value) === selectedValue;
                            return (
                                <Chip
                                    key={o.value}
                                    label={o.label ?? o.value}
                                    size="small"
                                    color={selected ? 'primary' : 'default'}
                                    variant={selected ? 'filled' : 'outlined'}
                                />
                            );
                        }) }
                    </Box>
                </Grid>
            );
        }
        else{
            // Fallback for field types without a dedicated renderer. Render by
            // value SHAPE, not type — SkincAIr surveys carry field types the
            // Hospital renderer doesn't know (diagnoses, etc.). Raw objects must
            // never reach JSX: that throws "Objects are not valid as a React
            // child" and crashes the whole submission view.
            let displayValue = valueRecord.value;
            if (typeof displayValue === 'string') {
                const trimmed = displayValue.trim();
                if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
                    try { displayValue = JSON.parse(trimmed); } catch (e) { /* keep raw string */ }
                }
            }
            const entryToText = (entry) => {
                if (entry === null || entry === undefined) return '';
                if (typeof entry !== 'object') return String(entry);
                // Known SkincAIr value-object shapes: {multioption}, {label}
                // (diagnoses), {text}, {value}, {name}. For coded entries
                // (e.g. ICD diagnoses) prepend the code. JSON as last resort.
                const label = entry.label ?? entry.multioption ?? entry.text
                    ?? entry.value ?? entry.name;
                if (label == null) return JSON.stringify(entry);
                const codeKey = Object.keys(entry).find((k) => /code$/i.test(k));
                const code = codeKey ? entry[codeKey] : null;
                return code ? `${code} — ${label}` : String(label);
            };
            if (Array.isArray(displayValue)) {
                displayValue = displayValue.map(entryToText).filter((s) => s !== '').join(', ');
            } else if (displayValue !== null && typeof displayValue === 'object') {
                displayValue = entryToText(displayValue);
            } else if (displayValue !== null && displayValue !== undefined
                       && Array.isArray(field.options) && field.options.length > 0) {
                // Single-value option field (select / radio / etc.): the stored
                // value is the option CODE — show the human label instead.
                const opt = field.options.find((o) => String(o.value) === String(displayValue));
                if (opt && opt.label != null) displayValue = opt.label;
            }
            return(
                <Grid item paddingLeft={3}>
                { showField &&
                    <Typography variant="h6" color="textPrimary">
                        {field.name}:
                    </Typography>
                }
                <Typography variant="body2" gutterBottom>
                    { displayValue }
                </Typography>
            </Grid>
            );
        }
    }
   
    
    function renderFieldGroups() {
        if (!props.fieldGroups || props.fieldGroups.length === 0) return null;

        return props.fieldGroups.map(fg => {
            const groupSchema = props.section?.fieldGroups?.find(g => g.id === fg.fieldGroupId);
            const groupName = groupSchema?.name || 'Group';
            const sortedFields = [...(groupSchema?.fields || [])].sort((a, b) => a.order - b.order);

            // SKIN-444: a closed group (D1 once the diagnosis is per lesion) with no recorded
            // instance is not an unanswered question; it has nothing to show.
            if (fg.instances.length === 0 && isClosedGroup(groupSchema)) return null;
            if (fg.instances.length === 0) {
                return (
                    <div key={fg.fieldGroupUuid}>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600, mt: 2, mb: 1, ml: 3 }}>
                            {groupName}
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 3 }}>-</Typography>
                    </div>
                );
            }

            return (
                <div key={fg.fieldGroupUuid}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600, mt: 2, mb: 1, ml: 3 }}>
                        {groupName}
                    </Typography>
                    {[...fg.instances]
                        .sort((a, b) => a.instanceNumber - b.instanceNumber)
                        .map((instance, idx) => (
                            <Card key={instance.groupRecordId || idx} variant="outlined" sx={{ p: 2, mb: 1, ml: 3 }}>
                                <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                                    #{idx + 1}
                                </Typography>
                                <Grid container direction="column">
                                {sortedFields.map(field => {
                                    const record = instance.surveyRecords.find(
                                        r => r.surveyField.id === field.id
                                    );
                                    return (
                                        <Grid item xs={12} key={field.id}>
                                            {renderValue(record, field, false, sortedFields.length > 1)}
                                        </Grid>
                                    );
                                })}
                                </Grid>
                            </Card>
                        ))}
                </div>
            );
        });
    }

    function renderSubmission(){
        const hasRecords = props.records.length > 0;
        const hasFieldGroups = props.fieldGroups && props.fieldGroups.length > 0;

        if(!hasRecords && !hasFieldGroups){
            return (
                <GridPadded container padding={!props.printMode}>
                    <Grid item xs={12}>
                        <Grid item paddingLeft={3}>
                            <Typography variant="body2" gutterBottom>
                                No records available
                            </Typography>
                        </Grid>
                    </Grid>
                </GridPadded>
            )
        }
        else{
            // SKIN-299 + SHER-208/243: iterate submission records, not section.fields.
            // SkincAIr surveys (e.g. NTD eCRF) define fields under section.fieldGroups
            // with section.fields === []. The old loop rendered zero rows while
            // props.records was populated — header only, blank body. Repeating
            // fieldGroups still render via renderFieldGroups() below.
            const recordsWithField = props.records.filter(r => r.surveyField);
            const flatRecords = [...recordsWithField]
                .sort((a, b) => (a.surveyField.order ?? 0) - (b.surveyField.order ?? 0));

            return(
                <GridPadded container padding={!props.printMode} direction="column" spacing={3}>
                    {
                        flatRecords.map(record => (
                            <Grid item xs={12} key={record.id}>
                                {
                                    renderValue(
                                        record,
                                        record.surveyField,
                                        record.surveyField.name === props.section.name,
                                        flatRecords.length > 1,
                                    )
                                }
                            </Grid>
                        ))
                    }
                    {renderFieldGroups()}
                </GridPadded>
            );
        }

    }
    if(!props.records || error){
        return (
            <Alert mb={4} severity="error">
                <Translate id="investigation.share.error.description" />
            </Alert>
        );
    }
    else{
        const dateCreated = new Date(props.updatedAt);
        return (
            <CardPadding padding={props.printMode}>
                <Grid container direction="column" spacing={3}>
                    <Grid item>
                        {
                            !props.noTitle &&
                            <HeaderSection>
                                <Typography variant="subtitle1" color="textPrimary" style={{ fontWeight: 600 }}>
                                    { props.section.name }
                                </Typography>
                                {
                                    (!props.printMode && props.onPrintSubmission) &&
                                    <IconButton 
                                        onClick={() => props.onPrintSubmission()}
                                        color="primary"
                                        size="small"
                                        sx={{
                                            marginLeft: 1
                                        }}
                                    >
                                        <PrintIcon />
                                    </IconButton>
                                }
                                {
                                    // TODO: When role-based permissions are implemented on the frontend,
                                    // restrict this edit button to roles with EDIT_SUBMISSIONS (or equivalent) permission.
                                    // SKIN-600: canEdit=false hides it (a dermatologist's web session, off its flagged visits).
                                    !props.printMode && props.canEdit !== false &&
                                    <ButtonEdit color="primary" onClick={() => editSection(props.section.uuid)} />
                                }
                            </HeaderSection>
                        }
                        
                    </Grid>
                    {
                        renderSubmission()
                    }
                    
                </Grid> 
            </CardPadding> 
        )
    }
    
}

ShowRecordsSection.propTypes = {
    section: PropTypes.object,
    submissions: PropTypes.array,
    forceEdit: PropTypes.bool,
    canEdit: PropTypes.bool,
    printMode: PropTypes.bool,
    callBackEditSubmission: PropTypes.func,
    records: PropTypes.array,
    fieldGroups: PropTypes.array,
    uuidResearcher: PropTypes.string,
    uuidInvestigation: PropTypes.string,
    idSubmission: PropTypes.number,
    updatedAt: PropTypes.string,
    onPrintSubmission: PropTypes.func,
};

export default withLocalize(ShowRecordsSection)